import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Copy, Edit, Eye, Filter, Mail, Plus, Search, Trash2, X } from 'lucide-react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Pagination } from '../../components/ui/Pagination';
import { Tooltip } from '../../components/ui/Tooltip';
import { ColumnCustomizer } from '../../components/ColumnCustomizer';
import { PermissionGate } from '../../components/PermissionGate';
import { PracticeDetailModal, practiceStatusVariant } from '../../components/training/practice/PracticeDetailModal';
import { TrainingEmailModal } from '../../components/training/TrainingEmailModal';
import { attendeeCount } from '../../utils/practices';
import { recipientGroups, sendPracticeEmail } from '../../utils/practiceEmail';
import { useFilters } from '../../hooks/useFilters';
import { useSearch } from '../../hooks/useSearch';
import { usePagination } from '../../hooks/usePagination';
import { usePractices } from '../../hooks/usePractices';
import { practicesStore } from '../../services/practicesStore';
import { useTrainingBilling } from '../../hooks/useTrainingBilling';
import { trainingCategories } from '../../data/trainingProducts';
import { practiceStatuses } from '../../data/practiceOptions';
import { requestedCourseLabel, requesterKindLabel, requesterName } from '../../utils/practices';
import { getTrainingProduct } from '../../utils/trainingCourses';
import { Practice, PracticeStatus } from '../../types/practice';
const columnsConfig = [{
  key: 'requester',
  label: 'Richiedente'
}, {
  key: 'course',
  label: 'Corso richiesto'
}, {
  key: 'participants',
  label: 'N° partecipanti'
}, {
  key: 'actions',
  label: 'Azioni'
}];
type SavedState = {
  saved?: 'created' | 'updated';
  id?: string;
} | null;
const statusOrder: Record<PracticeStatus, number> = {
  'In valutazione': 0,
  Positivo: 1,
  Negativo: 2
};
export function PracticesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    practices
  } = usePractices();
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Practice | null>(null);
  const [deleting, setDeleting] = useState<Practice | null>(null);
  const [emailing, setEmailing] = useState<Practice | null>(null);
  const [banner, setBanner] = useState((location.state as SavedState)?.saved);
  useEffect(() => {
    if (!banner) return;
    const timer = setTimeout(() => setBanner(undefined), 4000);
    return () => clearTimeout(timer);
  }, [banner]);

  // Searchable/filterable view of each practice, grouped by status:
  // In valutazione first, then Positivo, then Negativo (stable within each group)
  const rows = useMemo(() => practices.map(p => ({
    ...p,
    requester: requesterName(p),
    course: requestedCourseLabel(p)
  })).sort((a, b) => statusOrder[a.status] - statusOrder[b.status]), [practices]);
  // Highlighted rows: invoice paid but certificates still to be sent
  const billing = useTrainingBilling();
  const certificatesToSend = useMemo(() => {
    const paid = new Set(billing.filter(b => b.status === 'Fattura pagata').map(b => b.practiceId));
    return new Set(practices.filter(p => paid.has(p.id) && p.certificates === 'Da inviare').map(p => p.id));
  }, [billing, practices]);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData,
    activeFilterCount
  } = useFilters(rows);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filteredData, ['requester', 'course', 'productCode', 'courseCode']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const show = (key: string) => visibleColumns.includes(key);
  const shownColumns = columnsConfig.filter(c => show(c.key));
  const selected = practices.find(p => p.id === selectedId);
  const openEdit = (p: Practice) => navigate(`/training/pratiche/${p.id}/modifica`);
  return <Layout data-id="element-6112" data-mp="3kffpdol1rwgk">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-6113" data-mp="1nkj9rx20m8z6">
        <div data-id="element-6114" data-mp="1ilzo7xrc2z7c">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6115" data-mp="1ilgg18rhjp6i">Pratiche</h1>
          <p className="text-slate-500 mt-1" data-id="element-6116" data-mp="1q5imfb95ez0i">
            Iscrizioni ai corsi di gruppi di lavoratori o singoli individui.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-6117" data-mp="1v2r65g9y3b3v">
          <PermissionGate module="training" action="create" data-id="element-6118" data-mp="1zv26ulcif0xh">
            <Tooltip content={selected ? `Duplica la pratica di ${requesterName(selected)}` : 'Seleziona una pratica nella tabella'} data-id="element-6119" data-mp="1zvka41xlafew">
              <Button variant="flat" color="secondary" startContent={<Copy className="h-4 w-4" data-id="element-6121" data-mp="1c7art9zl49si" />} disabled={!selected} onClick={() => selected && navigate(`/training/pratiche/nuova?duplica=${selected.id}`)} data-id="element-6120" data-mp="178pupjfmq68a">
                Duplica pratica
              </Button>
            </Tooltip>
            <Button onClick={() => navigate('/training/pratiche/nuova')} startContent={<Plus className="h-4 w-4" data-id="element-6123" data-mp="xfrx1ifh6oh6" />} data-id="element-6122" data-mp="2wkrv98uaqmia">
              Nuova pratica
            </Button>
          </PermissionGate>
        </div>
      </div>

      <AnimatePresence data-id="element-6124" data-mp="sjkrhqqzrezr">
        {banner && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} role="status" className="mb-6 flex items-center gap-3 rounded-xl bg-green-50 border border-green-200 px-4 py-3" data-id="element-6125" data-mp="16hhjsayewz8m">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" data-id="element-6126" data-mp="nidftglq9vnt" />
            <p className="flex-1 text-sm font-medium text-green-700" data-id="element-6127" data-mp="njombo6k5him">
              {banner === 'created' ? 'Pratica creata con successo' : 'Pratica modificata con successo'}
            </p>
            <button type="button" aria-label="Chiudi messaggio" onClick={() => setBanner(undefined)} className="text-green-600 hover:text-green-800" data-id="element-6128" data-mp="3m8dmayvkf02q">
              <X className="h-4 w-4" data-id="element-6129" data-mp="26hl4vwjx3en2" />
            </button>
          </motion.div>}
      </AnimatePresence>

      <Card className="mb-6" noPadding data-id="element-6130" data-mp="1sqg3aowzoca3">
        <div className="p-4 flex flex-col gap-4" data-id="element-6131" data-mp="1snk1uqlxn5gw">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-6132" data-mp="1xnycmgex4tim">
            <div className="flex-1" data-id="element-6133" data-mp="1xmdfcj17ywh2">
              <Input placeholder="Cerca richiedente, corso, codice prodotto o codice corso..." startContent={<Search className="h-4 w-4" data-id="element-6135" data-mp="2pfdvq0h9ny43" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-6134" data-mp="2le2dwh4mwh6g" />
            </div>
            <div className="flex gap-2" data-id="element-6136" data-mp="2gdo6906y398y">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-6138" data-mp="14xptal8nc32g" />} data-id="element-6137" data-mp="100zee18kvdim">
                Filtri{' '}
                {activeFilterCount > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-6139" data-mp="1roaq445p8tbf">
                    {activeFilterCount}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="training-practices" lockedColumns={['requester']} data-id="element-6140" data-mp="1hqmjafutjvcw" />
            </div>
          </div>
          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100" data-id="element-6141" data-mp="25n0nzm0cex0u">
              <Select label="Stato della pratica" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, ...practiceStatuses.map(s => ({
            value: s,
            label: s
          }))]} data-id="element-6142" data-mp="20oyus897dhbn" />
              <Select label="Macrocategoria" value={filters.category || ''} onChange={e => setFilter('category', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, ...trainingCategories.map(c => ({
            value: c,
            label: c
          }))]} data-id="element-6143" data-mp="35ndpq4l0678b" />
              <Select label="Stato del corso" value={filters.courseStatus || ''} onChange={e => setFilter('courseStatus', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Programmato',
            label: 'Programmato'
          }, {
            value: 'Eseguito',
            label: 'Eseguito'
          }, {
            value: 'On-demand',
            label: 'On-demand'
          }]} data-id="element-6144" data-mp="3omzv09z7if7w" />
              <Select label="Attestati" value={filters.certificates || ''} onChange={e => setFilter('certificates', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Da inviare',
            label: 'Da inviare'
          }, {
            value: 'Inviati',
            label: 'Inviati'
          }]} data-id="element-6145" data-mp="c38ktzybkl56" />
              <div className="col-span-full flex justify-end" data-id="element-6146" data-mp="3pi5t9it8bq5a">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-6148" data-mp="2ehivk95m49k" />} data-id="element-6147" data-mp="3pk0587miw7x8">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-6149" data-mp="245m7e7fsiopo">
        <div className="overflow-x-auto" data-id="element-6150" data-mp="293nsexws0y3s">
          <table className="w-full text-left border-collapse table-auto" data-id="element-6151" data-mp="294p2p05yxgkg">
            <thead data-id="element-6152" data-mp="21kmwaxob26qg">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-6153" data-mp="21l6emfhvax50">
                <th className="pl-6 pr-2 py-4 w-10" data-id="element-6154" data-mp="1ws3fzsy8gxhy">
                  <span className="sr-only" data-id="element-6155" data-mp="1rtjxk2ium3uc">Seleziona</span>
                </th>
                {shownColumns.map(col => <th key={col.key} className={`px-4 py-4 whitespace-nowrap ${col.key === 'participants' ? 'text-right' : ''} ${col.key === 'actions' ? 'pr-6 text-right' : ''}`} data-id="element-6156" data-mp="1mwtgbbvb527c">
                    {col.label}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6157" data-mp="26jhp8sfzlboa">
              {paginatedData.length === 0 && <tr data-id="element-6158" data-mp="z65urnsgye2b">
                  <td colSpan={shownColumns.length + 1} className="px-4 py-16 text-center text-sm text-slate-500" data-id="element-6159" data-mp="z6pcp4i4d2gc">
                    {practices.length === 0 ? 'Nessuna pratica inserita.' : 'Nessuna pratica corrisponde alla ricerca o ai filtri.'}
                  </td>
                </tr>}
              {paginatedData.map(p => {
              const isSelected = selectedId === p.id;
              const product = getTrainingProduct(p.productCode);
              const toSend = certificatesToSend.has(p.id);
              return <tr key={p.id} className={`transition-colors ${isSelected ? 'bg-orange-50/60' : toSend ? 'bg-yellow-100 hover:bg-yellow-200/70' : 'hover:bg-slate-50/80'}`} title={toSend ? 'Fattura pagata: attestati da inviare' : undefined} data-id="element-6160" data-mp="ilgn2l6fa3pe">
                    <td className="pl-6 pr-2 py-4" data-id="element-6161" data-mp="3v22cfs5pri27">
                      <input type="checkbox" aria-label={`Seleziona pratica di ${p.requester}`} className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={isSelected} onChange={() => setSelectedId(isSelected ? null : p.id)} data-id="element-6162" data-mp="3ce1mjikrkfmk" />
                    </td>
                    {show('requester') && <td className="px-4 py-4" data-id="element-6163" data-mp="3vb2u9b5pctu7">
                        <p className="text-sm font-medium text-seform-navy" data-id="element-6164" data-mp="3qd9lcad0llq8">
                          {p.requester}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2" data-id="element-6165" data-mp="8263la0406wg">
                          {requesterKindLabel(p)}
                          <Badge variant={practiceStatusVariant[p.status]} size="sm" data-id="element-6166" data-mp="hzklle1ol3qv">
                            {p.status}
                          </Badge>
                        </p>
                        {toSend && <p className="text-xs font-medium text-yellow-800 mt-1" data-id="element-6167" data-mp="x114e9gpr7hk">
                            Fattura pagata · Attestati da inviare
                          </p>}
                      </td>}
                    {show('course') && <td className="px-4 py-4" data-id="element-6168" data-mp="1asfpqmy3fkrs">
                        <p className="text-sm text-slate-700" data-id="element-6169" data-mp="e2yaphd80mdf">{p.course}</p>
                        <p className="text-xs text-slate-400 mt-0.5" data-id="element-6170" data-mp="e4rhcwnu36lf">
                          {product?.type && `${product.type} · `}
                          <span className="font-mono" data-id="element-6171" data-mp="n2trq2tgjbld">{p.productCode}</span>
                        </p>
                      </td>}
                    {show('participants') && <td className="px-4 py-4 text-sm text-slate-700 text-right tabular-nums" data-id="element-6172" data-mp="1yj1n9kah1mnj">
                        {attendeeCount(p) ?? '-'}
                      </td>}
                    {show('actions') && <td className="px-4 py-4 pr-6" data-id="element-6173" data-mp="3nz4a145z4n99">
                        <div className="flex justify-end gap-1" data-id="element-6174" data-mp="3j011vaup60x2">
                          <Tooltip content="Vedi" data-id="element-6175" data-mp="3j2nhlxrqrmqd">
                            <Button variant="light" size="sm" className="!px-1" aria-label="Vedi pratica" onClick={() => setViewing(p)} data-id="element-6176" data-mp="3e8ss2dpggdjd">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-6177" data-mp="308hz954l4ovk" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Invia email" data-id="element-6178" data-mp="2qbwv0m0e3hss">
                            <Button variant="light" size="sm" className="!px-1" aria-label="Invia email al cliente" onClick={() => setEmailing(p)} data-id="element-6179" data-mp="2qdgc2etvdwvm">
                              <Mail className="h-4 w-4 text-slate-500" data-id="element-6180" data-mp="2iyxlxpceagzm" />
                            </Button>
                          </Tooltip>
                          <PermissionGate module="training" action="edit" data-id="element-6181" data-mp="293cgtic150ln">
                            <Tooltip content="Modifica" data-id="element-6182" data-mp="2e1e7aobsc275">
                              <Button variant="light" size="sm" className="!px-1" aria-label="Modifica pratica" onClick={() => openEdit(p)} data-id="element-6183" data-mp="2e1xdj7s2p25c">
                                <Edit className="h-4 w-4 text-slate-500" data-id="element-6184" data-mp="2ws5gnpvrvslj" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                          <PermissionGate module="training" action="delete" data-id="element-6185" data-mp="1ld08w7gsb3v2">
                            <Tooltip content="Elimina" data-id="element-6186" data-mp="1lavv8xt32qhl">
                              <Button variant="light" size="sm" className="!px-1" aria-label="Elimina pratica" onClick={() => setDeleting(p)} data-id="element-6187" data-mp="1dso6o0reasng">
                                <Trash2 className="h-4 w-4 text-red-500" data-id="element-6188" data-mp="1itbxalbpp7h5" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                        </div>
                      </td>}
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-6189" data-mp="1011ae0xgdk4o">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-6190" data-mp="cci4fs1vljfr" />
        </div>
      </Card>

      <PracticeDetailModal practice={viewing} onClose={() => setViewing(null)} onEdit={openEdit} onEmail={p => {
      setViewing(null);
      setEmailing(p);
    }} data-id="element-6191" data-mp="3i1kqn7mtxi1a" />
      <TrainingEmailModal isOpen={Boolean(emailing)} title="Email al cliente" context={emailing ? requestedCourseLabel(emailing) : undefined} groups={emailing ? recipientGroups([emailing]) : []} onClose={() => setEmailing(null)} onSend={body => emailing && sendPracticeEmail([emailing], body)} data-id="element-6192" data-mp="3vz9aqd92ol5a" />
      <Modal isOpen={Boolean(deleting)} onClose={() => setDeleting(null)} title="Elimina pratica" size="md" data-id="element-6193" data-mp="ek2h8vres2yr">
        {deleting && <>
            <p className="text-sm text-slate-600" data-id="element-6194" data-mp="nkh4i0nxns4w">
              Vuoi eliminare la pratica di{' '}
              <span className="font-semibold text-slate-800" data-id="element-6195" data-mp="sjkapofng4eg">
                {requesterName(deleting)}
              </span>
              ? L'operazione non può essere annullata.
            </p>
            <div className="flex justify-end gap-3 pt-6" data-id="element-6196" data-mp="280f9lr6sjryt">
              <Button variant="flat" color="default" onClick={() => setDeleting(null)} data-id="element-6197" data-mp="23zvcgnm7woco">
                Annulla
              </Button>
              <Button color="danger" onClick={() => {
            practicesStore.remove(deleting.id);
            if (selectedId === deleting.id) setSelectedId(null);
            setDeleting(null);
          }} data-id="element-6198" data-mp="1z1k4yjjeapxm">
                Elimina
              </Button>
            </div>
          </>}
      </Modal>
    </Layout>;
}